import React from 'react';
import { User } from 'oidc-client-ts';
import { AuthProvider } from 'react-oidc-context';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import {
  ErrorsLayout,
  InGameLayout,
  PublicLayout,
  PreGameLayout,
  FacilitatorLayout,
  TiloFacilitatorLayout,
  LeaderboardLayout
} from './layouts';
import {
  ChatPage,
  Feedback,
  TeamsPage,
  PlayersPage,
  DashboardPage,
  TaskScoringPage,
  PushContentPage
} from './pages/facilitator';
import GameLayout from './layouts/game';
import instanceConfig from './instance';
import { PageNotFound } from './pages/errors';
import { QRPage } from './pages/facilitator/qr';
import { TiloFacilitatorPage } from './pages/tilo';
import { ArPyramidPage } from './pages/ar-pyramid';
import { Groups } from './pages/facilitator/groups';
import { SigninOidcPage } from './pages/signin-oidc';
import { ReelsPage } from './pages/facilitator/reels';
import { ArPyramidLayout } from './layouts/ar-pyramid';
import { MapView } from './pages/facilitator/map-view';
import { AllTasks } from './pages/facilitator/all-task';
import { Settings } from './pages/facilitator/settings';
import FeedbackLayout from './layouts/feedback/feedback';
import SplashScreen from './pages/pre-game/splash-screen';
import { VersionPanel } from './components/version-panel';
import { GameLobbyPage, GameMapPage } from './pages/in-game';
import LeaderboardPage from './pages/leaderboard/leaderboard';
import { FeedbackForm } from './pages/feedback/feedback-form';
import { VersionInfoProvider } from './contexts/version-info';
import { gameCodeLoader } from './middlewares/gamecode-loader';
import { InstanceProvider } from './contexts/instance-provider';
import { FeedbackGames } from './pages/feedback/feedback-games';
import { PreloadAssetPage } from './pages/pre-game/preload-asset';
import { InventoryBankPage } from './pages/facilitator/inventory';
import { PrivacyPolicyPage, TermsOfUsePage } from './pages/public';
import { EnterGameCodePage, JoinGamePage } from './pages/pre-game';
import { FeedbackPlayerName } from './pages/feedback/feedback-player-name';
import { PlayerAssessmentPage } from './pages/assessment/player-assessment';

declare global {
  interface Window {
    _env_: any;
  }
}

function App() {
  const oidcConfig = {
    onSigninCallback: (_user: User | void): void => {
      window.history.replaceState(
        {},
        document.title,
        ((_user as User).state as any).returnUrl
      );
      window.location.reload();
    },
    authority:
      window?._env_?.REACT_APP_AUTHORITY || process.env.REACT_APP_AUTHORITY,
    client_id:
      window?._env_?.REACT_APP_CLIENT_ID || process.env.REACT_APP_CLIENT_ID,
    redirect_uri:
      window?._env_?.REACT_APP_REDIRECT_URI ||
      process.env.REACT_APP_REDIRECT_URI,
    scope: 'offline_access openid profile',
    silent_redirect_uri:
      window?._env_?.REACT_APP_SILENT_REDIRECT_URI ||
      process.env.REACT_APP_SILENT_REDIRECT_URI,
    loadUserInfo: true,
    post_logout_redirect_uri: `${
      window?._env_?.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL
    }/games`
  };

  // service worker
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js');
    });
  }

  // prevent iOS zoom
  React.useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#map') && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    return () => document.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const router = createBrowserRouter([
    {
      element: <PublicLayout />,
      children: [
        { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
        { path: '/terms-of-use', element: <TermsOfUsePage /> }
      ]
    },

    {
      element: <ErrorsLayout />,
      children: [{ path: '*', element: <PageNotFound /> }]
    },

    { path: '/', element: <EnterGameCodePage /> },
    { path: '/games', element: <EnterGameCodePage /> },

    {
      path: '/arpyramid/:gameCode',
      loader: gameCodeLoader,
      element: <ArPyramidLayout />,
      children: [{ index: true, element: <ArPyramidPage /> }]
    },

    {
      element: <GameLayout />,
      children: [
        {
          path: '/preload/:gameCode',
          loader: gameCodeLoader,
          element: <PreGameLayout />,
          children: [{ index: true, element: <PreloadAssetPage /> }]
        },

        {
          path: '/games/:gameCode',
          loader: gameCodeLoader,
          children: [
            {
              element: <PreGameLayout />,
              children: [
                { index: true, element: <JoinGamePage /> },
                { path: 'splash', element: <SplashScreen /> }
              ]
            },

            {
              element: <InGameLayout />,
              children: [
                { path: 'lobby', element: <GameLobbyPage /> },
                { path: 'map', element: <GameMapPage /> }
              ]
            },

            {
              path: 'assessment/:page?',
              element: <PlayerAssessmentPage />
            }
          ]
        }
      ]
    },

    {
      path: '/games/:gameCode/leaderboard',
      loader: gameCodeLoader,
      element: <LeaderboardLayout />,
      children: [{ index: true, element: <LeaderboardPage /> }]
    },

    {
      path: '/games/:gameCode/facilitator/reels',
      loader: gameCodeLoader,
      element: <LeaderboardLayout isFacilitator />,
      children: [{ index: true, element: <ReelsPage /> }]
    },

    {
      path: '/games/:gameCode/facilitator',
      loader: gameCodeLoader,
      element: <FacilitatorLayout />,
      children: [
        { index: true, element: <DashboardPage /> },
        { path: 'map', element: <MapView /> },
        { path: 'task-scoring', element: <TaskScoringPage /> },
        { path: 'all-tasks', element: <AllTasks type="Task" /> },
        {
          path: 'all-on-demand-tasks',
          element: <AllTasks type="OnDemandTask" />
        },
        {
          path: 'inventory-bank',
          element: <InventoryBankPage />
        },
        { path: 'chat', element: <ChatPage /> },
        { path: 'push-content', element: <PushContentPage /> },
        { path: 'players', element: <PlayersPage /> },
        { path: 'teams', element: <TeamsPage /> },
        { path: 'groups', element: <Groups /> },
        { path: ':qrPage', element: <QRPage /> },
        { path: 'settings', element: <Settings /> },
        { path: 'feedback', element: <Feedback /> }
      ]
    },

    {
      path: '/tilo/facilitator/:gameId',
      element: <TiloFacilitatorLayout />,
      children: [{ index: true, element: <TiloFacilitatorPage /> }]
    },

    { path: '/signin-oidc', element: <SigninOidcPage /> },

    {
      path: '/feedbacks/:feedbackCode',
      element: <FeedbackLayout />,
      children: [
        { index: true, element: <FeedbackPlayerName /> },
        { path: 'games', element: <FeedbackGames /> },
        { path: 'form', element: <FeedbackForm /> }
      ]
    }
  ]);

  return (
    <InstanceProvider {...instanceConfig}>
      <VersionInfoProvider>
        <AuthProvider {...oidcConfig}>
          <RouterProvider router={router} />
          <VersionPanel />
        </AuthProvider>
      </VersionInfoProvider>
    </InstanceProvider>
  );
}

/*
 * /privacy-policy
 * /terms-of-use
 * /games
 * /games/:gameCode
 * /games/:gameCode/lobby
 * /games/:gameCode/map
 * /games/:gameCode/facilitator
 * /games/:gameCode/facilitator/example-page
 * /*
 */

export default App;
